import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { Spinner } from "reactstrap"
import { getDatafonoResumen, descargarPagosDesgloseExport } from "../api"
import { formatCurrency, formatNumber, formatFechaCorta, rangoDelMes, colorPara } from "../helpers"

const Kpi = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{ flex: "1 1 180px", minWidth: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "16px 18px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <div style={{ width: 34, height: 34, borderRadius: 9, background: `${accent}1a`, color: accent, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, flexShrink: 0 }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</span>
    </div>
    <div style={{ fontSize: 20, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 3 }}>{sub}</div>}
  </div>
)
Kpi.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

const MiniTabla = ({ titulo, icon, dimKey, dimLabel, rows, esFecha }) => {
  const ordenadas = useMemo(
    () => [...(rows || [])].sort((a, b) => (Number(b.valor) || 0) - (Number(a.valor) || 0)),
    [rows],
  )
  const totalValor = ordenadas.reduce((s, r) => s + (Number(r.valor) || 0), 0)
  return (
    <div style={{ flex: "1 1 320px", minWidth: 0, background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 18 }}>
      <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
        <i className={`mdi ${icon} me-2 text-primary`} />{titulo}
      </h6>
      <div style={{ maxHeight: 300, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead style={{ position: "sticky", top: 0, background: "#f8fafc" }}>
            <tr>
              <th style={thMini}>{dimLabel}</th>
              <th style={{ ...thMini, textAlign: "right" }}>Trans.</th>
              <th style={{ ...thMini, textAlign: "right" }}>Valor</th>
            </tr>
          </thead>
          <tbody>
            {ordenadas.length === 0 && (
              <tr><td colSpan={3} style={{ padding: 20, textAlign: "center", color: "#94a3b8" }}>Sin datos</td></tr>
            )}
            {ordenadas.map((r, i) => {
              const pct = totalValor > 0 ? (Number(r.valor) || 0) / totalValor * 100 : 0
              return (
                <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                  <td style={{ padding: "8px 10px", color: "#0f172a" }}>
                    {!esFecha && <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: colorPara(i), marginRight: 8 }} />}
                    {esFecha ? formatFechaCorta(r[dimKey]) : (r[dimKey] || "—")}
                  </td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: "#64748b" }}>{formatNumber(r.transacciones)}</td>
                  <td style={{ padding: "8px 10px", textAlign: "right", color: "#059669", fontWeight: 600 }}>
                    {formatCurrency(r.valor)}
                    <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 400 }}>{pct.toFixed(1)}%</div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
MiniTabla.propTypes = { titulo: PropTypes.string, icon: PropTypes.string, dimKey: PropTypes.string, dimLabel: PropTypes.string, rows: PropTypes.array, esFecha: PropTypes.bool }

const DatafonoTab = ({ filtros }) => {
  const [campoFecha, setCampoFecha] = useState("fecha")
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [exportando, setExportando] = useState(null)

  const rango = useMemo(() => {
    if (!filtros) return {}
    const fecha_inicio = filtros.fecha_inicio || rangoDelMes(filtros.year, filtros.month).fecha_inicio
    const fecha_fin = filtros.fecha_fin || rangoDelMes(filtros.year, filtros.month).fecha_fin
    return { fecha_inicio, fecha_fin }
  }, [filtros])

  const cargar = useCallback(async () => {
    if (!filtros?.nitemisor) return
    setLoading(true)
    setError("")
    try {
      const { data: response } = await getDatafonoResumen({
        nitemisor: filtros.nitemisor,
        fecha_inicio: rango.fecha_inicio,
        fecha_fin: rango.fecha_fin,
        sucursal: filtros.sucursal,
        campo_fecha: campoFecha,
      })
      if (response?.success === false) throw new Error(response.error || "El servidor reportó un error.")
      setData(response)
    } catch (err) {
      setError(err.response?.data?.error || err.message || "No se pudo cargar el resumen del datáfono.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [filtros, rango, campoFecha])

  useEffect(() => { cargar() }, [cargar])

  const handleExport = async formato => {
    if (!filtros?.nitemisor || exportando) return
    setExportando(formato)
    setError("")
    try {
      await descargarPagosDesgloseExport({
        nitemisor: filtros.nitemisor,
        fuente: "datafono",
        agrupar_por: "sucursal_medio_dia",
        fecha_inicio: rango.fecha_inicio,
        fecha_fin: rango.fecha_fin,
        sucursal: filtros.sucursal,
        campo_fecha: campoFecha,
        formato,
      })
    } catch (err) {
      setError(err.message || "Error al exportar.")
    } finally {
      setExportando(null)
    }
  }

  const resumen = data?.resumen || null
  const sinDatafono = data && (!resumen || Number(resumen.total_transacciones) === 0)

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Barra: campo fecha + export */}
      <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 16px", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <div style={{ fontSize: 13, color: "#475569" }}>
          <i className="mdi mdi-credit-card-check-outline me-1 text-primary" />
          Transacciones confirmadas por <strong>nuestros datáfonos</strong>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textTransform: "uppercase" }}>Fecha por</span>
            <select value={campoFecha} onChange={e => setCampoFecha(e.target.value)} style={{ fontSize: 12, padding: "5px 8px", borderRadius: 6, border: "1px solid #cbd5e1", height: 32 }}>
              <option value="fecha">Fecha documento</option>
              <option value="fecha_pago">Fecha de pago</option>
            </select>
          </div>
          <div className="btn-group btn-group-sm">
            <button type="button" className="btn btn-outline-success" onClick={() => handleExport("xlsx")} disabled={!!exportando || sinDatafono || !data}>
              {exportando === "xlsx" ? <><Spinner size="sm" className="me-1" /> …</> : <><i className="mdi mdi-file-excel me-1" /> Excel</>}
            </button>
            <button type="button" className="btn btn-outline-primary" onClick={() => handleExport("csv")} disabled={!!exportando || sinDatafono || !data}>
              {exportando === "csv" ? <><Spinner size="sm" className="me-1" /> …</> : <><i className="mdi mdi-file-delimited me-1" /> CSV</>}
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ padding: "14px 18px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: 10, display: "flex", alignItems: "center", gap: 10 }}>
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={cargar}>Reintentar</button>
        </div>
      )}

      {loading && !data && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 80, textAlign: "center", color: "#64748b", border: "1px solid #e2e8f0" }}>
          <Spinner color="primary" />
          <div className="mt-2">Cargando datáfono…</div>
        </div>
      )}

      {/* Empty state: sin servicio de datáfono */}
      {sinDatafono && (
        <div style={{ background: "linear-gradient(135deg, #f8fafc, #ffffff)", border: "1px dashed #cbd5e1", borderRadius: 14, padding: "50px 40px", textAlign: "center" }}>
          <i className="mdi mdi-credit-card-off-outline" style={{ fontSize: 52, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>Sin transacciones de datáfono en el período</h5>
          <p style={{ color: "#64748b", maxWidth: 480, margin: "8px auto 0", fontSize: 14 }}>
            El <strong>datáfono</strong> es un servicio adicional que confirma la porción tarjeta desde el terminal.
            Si este emisor lo tiene contratado y esperabas datos, prueba con otro rango de fechas.
            Los <strong>medios de pago</strong> (POS) del resto de tabs sí aplican para todos los clientes.
          </p>
        </div>
      )}

      {/* Resumen con datos */}
      {resumen && !sinDatafono && (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            <Kpi icon="mdi-swap-horizontal" label="Transacciones" value={formatNumber(resumen.total_transacciones)} accent="#3b82f6" />
            <Kpi icon="mdi-cash-multiple" label="Valor (neto)" value={formatCurrency(resumen.total_valor)} accent="#10b981" sub="Las devoluciones restan" />
            <Kpi icon="mdi-calculator" label="Ticket promedio" value={formatCurrency(resumen.ticket_promedio)} accent="#8b5cf6" />
            <Kpi icon="mdi-store-outline" label="Sucursales" value={formatNumber(resumen.sucursales)} accent="#f59e0b"
              sub={[resumen.dias ? `${resumen.dias} días` : null, resumen.medios ? `${resumen.medios} medios` : null].filter(Boolean).join(" · ")} />
            <Kpi icon="mdi-calendar-range" label="Rango con datos" value={`${formatFechaCorta(resumen.primera_fecha)} → ${formatFechaCorta(resumen.ultima_fecha)}`} accent="#06b6d4" />
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
            <MiniTabla titulo="Por día" icon="mdi-calendar" dimKey="fecha" dimLabel="Fecha" rows={resumen.por_dia} esFecha />
            <MiniTabla titulo="Por sucursal" icon="mdi-store" dimKey="sucursal" dimLabel="Sucursal" rows={resumen.por_sucursal} />
            <MiniTabla titulo="Por medio" icon="mdi-credit-card-multiple" dimKey="medio" dimLabel="Medio" rows={resumen.por_medio} />
          </div>
        </>
      )}
    </div>
  )
}

DatafonoTab.propTypes = { filtros: PropTypes.object }

const thMini = { padding: "7px 10px", textAlign: "left", fontSize: 10, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.3, borderBottom: "1px solid #e2e8f0" }

export default DatafonoTab
